import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { incrementAnalyticsView } from '../utils/api';
import { useUser } from '../context/UserContext';

export const useTerminalAnalyticsTracking = (enabled = true) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { email } = useUser();
  const hasTrackedViewRef = useRef(false);
  const terminalOpenTimeRef = useRef(null);
  const wasFetchingRef = useRef(false);

  const isTerminalPage = location.pathname === '/analytics';

  const isFetching = useIsFetching({
    predicate: (query) => {
      const queryKey = query.queryKey?.[0];
      const chartQueryKeys = [
        'terminalPriceChartData',
        'priceChartData',
        'dailyVolumeChartData',
        'historicVolumeChartData',
        'predictionsChartData',
        'clusterChartData',
        'producingRegionsData',
        'fobPriceAvg',
        'fobPriceByOrigin',
        'fobSeasonality',
        'marketShareChart',
        'marketShareSummary',
        'allChartsData',
      ];
      return chartQueryKeys.some(key => queryKey?.includes(key));
    },
  });

  useEffect(() => {
    if (isTerminalPage) {
      hasTrackedViewRef.current = false;
      terminalOpenTimeRef.current = Date.now();
      wasFetchingRef.current = false;
    }
  }, [isTerminalPage]);

  useEffect(() => {
    if (!enabled || !isTerminalPage || !email || hasTrackedViewRef.current) {
      return;
    }

    if (isFetching > 0) {
      wasFetchingRef.current = true;
    } 
    else if (wasFetchingRef.current && isFetching === 0) {
      const checkTimeout = setTimeout(() => {
        const queryCache = queryClient.getQueryCache();
        const chartQueryKeys = [
          'terminalPriceChartData',
          'priceChartData',
          'dailyVolumeChartData',
          'historicVolumeChartData',
          'predictionsChartData',
          'clusterChartData',
          'producingRegionsData',
          'fobPriceAvg',
          'fobPriceByOrigin',
          'fobSeasonality',
          'marketShareChart',
          'marketShareSummary',
          'allChartsData',
        ];

        let hasFreshData = false;
        let detectedCommodity = null;

        queryCache.getAll().forEach((query) => {
          const queryKey = query.queryKey;
          const queryName = queryKey?.[0];
          
          if (chartQueryKeys.some(key => queryName?.includes(key))) {
            const state = query.state;
            const commodityFromQuery = queryKey?.[2];
            
            if (state.data && state.dataUpdatedAt && terminalOpenTimeRef.current) {
              const timeSinceUpdate = Date.now() - state.dataUpdatedAt;
              const timeSinceTerminalOpen = Date.now() - terminalOpenTimeRef.current;
              
              if (
                state.dataUpdatedAt > terminalOpenTimeRef.current &&
                timeSinceUpdate < 30000 &&
                timeSinceTerminalOpen < 60000
              ) {
                hasFreshData = true;
                if (commodityFromQuery && typeof commodityFromQuery === 'string' && commodityFromQuery.trim() && !detectedCommodity) {
                  detectedCommodity = commodityFromQuery;
                }
              }
            }
          }
        });

        if (hasFreshData && detectedCommodity) {
          hasTrackedViewRef.current = true;
          incrementAnalyticsView(detectedCommodity);
        }
        
        wasFetchingRef.current = false;
      }, 100);

      return () => clearTimeout(checkTimeout);
    }
  }, [isFetching, enabled, isTerminalPage, email, queryClient]);
};
